/**
 * Board Toolbar
 *
 * Shared filter lockup rendered above every board (Tasks, Products, Accounts).
 * Built on the `FilterToolbar` primitives from `@chatsdet/ui` — the same
 * bordered search-well + filter-menu system used by the Flows and Issues list
 * pages — so all three boards share one filtering chrome instead of each
 * hand-rolling its own row.
 *
 * Layout: a full-width lockup that fuses with the board below it (`attached`).
 * Search sits flush on the left, board-specific filters follow, the primary
 * action (the create dialog) is pushed to the far right by a spacer, and any
 * active-filter chips wrap to a clearable second row inside the lockup.
 *
 * @module features/boards/BoardToolbar
 */

import React from 'react';
import {
  FilterToolbar,
  FilterToolbarSearch,
  FilterToolbarSpacer,
  FilterToolbarChips,
  type FilterToolbarChip,
} from '@chatsdet/ui';

/**
 * Surface styling for the board area beneath a `BoardToolbar`. A soft muted
 * panel — fused with the (matching) toolbar above it into one container — so
 * the white cards inside clearly stand out against it. Bordered on the sides
 * and bottom (the top edge fuses with the attached toolbar) with rounded
 * bottom corners to close the lockup. Pass to a board's scroll container via
 * its `className`. Keep in sync with the toolbar fill in `BoardToolbar`.
 */
export const boardSurfaceClassName =
  'border border-border border-t-0 rounded-b-md bg-muted/60';

export interface BoardToolbarProps {
  /** Current search text. Omit `onSearchChange` to hide the search well. */
  searchValue?: string;
  /** Called with the raw input value. Boards own trimming / param semantics. */
  onSearchChange?: (value: string) => void;
  /** Placeholder for the search input, e.g. "Search tasks". */
  searchPlaceholder?: string;
  /** Accessible label for the search input. Defaults to the placeholder. */
  searchAriaLabel?: string;
  /** Board-specific filters — usually `ToolbarFilterMenu`s. */
  filters?: React.ReactNode;
  /** Primary action (the create dialog), right-aligned inside the lockup. */
  actions?: React.ReactNode;
  /** Active filters as a clearable chips row below the controls. */
  chips?: FilterToolbarChip[];
  /** Remove a single active filter by its chip id. */
  onChipRemove?: (chipId: string) => void;
  /**
   * Fuse with the board below (square bottom corners, no bottom border) so the
   * toolbar reads as the board's header. Defaults to true.
   */
  attached?: boolean;
}

/**
 * Unified board toolbar: a full-width `FilterToolbar` lockup holding search,
 * board-specific filters, the right-aligned primary action, and a clearable
 * active-filter chips row — fused with the board beneath it.
 */
export function BoardToolbar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search',
  searchAriaLabel,
  filters,
  actions,
  chips,
  onChipRemove,
  attached = true,
}: BoardToolbarProps) {
  return (
    <FilterToolbar attached={attached} className="shrink-0 bg-muted/60">
      {onSearchChange && (
        <FilterToolbarSearch
          aria-label={searchAriaLabel ?? searchPlaceholder}
          placeholder={searchPlaceholder}
          value={searchValue ?? ''}
          onChange={onSearchChange}
          className="w-80 [&_input]:!bg-card"
        />
      )}
      {filters}
      {actions && (
        <>
          <FilterToolbarSpacer />
          {actions}
        </>
      )}
      {chips && onChipRemove && <FilterToolbarChips chips={chips} onRemove={onChipRemove} />}
    </FilterToolbar>
  );
}
